import head from 'lodash/head'
import { DateTime } from 'luxon'
import { QuoteReservation, ReservationIdentifier } from '@/models/quote'
import { License } from '@/store/licenseSlice'

interface CheckoutService {
  day: number
  endTime: string
  price: number
  quantity: number
  serviceId: number
  startTime: string
  timeScheduleName: string
}

export type ItemGroup = {
  items: CheckoutService[]
  startDate: number // Unix epoch in seconds of the first day in the group.
  endDate: number // Unix epoch in seconds of the last day in the group.
  startTime: string
  endTime: string
  quantity: number
  totalPrice: number
}

export function getGroupLabel({
  dayDifference,
  group,
  license,
  locale,
}: {
  dayDifference: number
  group: ItemGroup
  license: License | null
  locale: string
}): string | null {
  if (dayDifference === 1 || !license?.featureFlags.timeSchedulesEnabled) {
    return null
  }

  const startDate = DateTime.fromSeconds(group.startDate).setLocale(locale)

  if (group.startDate === group.endDate) {
    return `${startDate.day} ${startDate.monthShort}`
  }

  // check if same month
  const endDate = DateTime.fromSeconds(group.endDate).setLocale(locale)

  if (startDate.month === endDate.month) {
    return `${startDate.day} - ${endDate.day} ${endDate.monthShort}`
  }

  return `${startDate.day} ${startDate.monthShort} - ${endDate.day} ${endDate.monthShort}`
}

export function getItemGroups(
  cartReservations: QuoteReservation[],
  reservation: ReservationIdentifier,
  serviceId: number
): ItemGroup[] {
  const foundReservation = cartReservations.find(
    (r) =>
      r.spotType === reservation.spotType && r.spotName === reservation.spotName
  )

  if (!foundReservation) {
    return []
  }

  const boughtServices = foundReservation.boughtServices[serviceId]
  if (!boughtServices) {
    return []
  }

  const services: CheckoutService[] = []

  Object.values(boughtServices).forEach((boughtService) => {
    const sortedDays = [...boughtService.days].sort((a, b) => a - b)
    sortedDays.forEach((day) => {
      services.push({
        day,
        endTime: boughtService.timeSchedule.endTime,
        price: boughtService.timeSchedule.price,
        quantity: boughtService.bought,
        serviceId: boughtService.serviceId,
        startTime: boughtService.timeSchedule.startTime,
        timeScheduleName: boughtService.timeSchedule.name,
      })
    })
  })

  return groupItems(services)
}

function groupItems(items: CheckoutService[]): ItemGroup[] {
  if (items.length === 0) {
    return []
  }

  // Create a deep copy of the items array to avoid direct state mutation
  const groups: ItemGroup[] = []

  const firstItem = head(items)
  if (!firstItem) {
    return []
  }

  // Initialize the first group
  let currentGroup: ItemGroup = {
    endDate: firstItem.day,
    endTime: firstItem.endTime,
    items: [firstItem],
    quantity: firstItem.quantity,
    startDate: firstItem.day,
    startTime: firstItem.startTime,
    totalPrice: firstItem.price,
  }

  // Store the reference start and end times for the current group
  let referenceStartTime = items[0].startTime
  let referenceEndTime = items[0].endTime

  // Iterate through the sorted items starting from the second item
  for (let i = 1; i < items.length; i++) {
    const currentItem = items[i]
    const previousItemInGroup =
      currentGroup.items[currentGroup.items.length - 1]

    // Convert 'day' epoch seconds to Luxon DateTime objects for comparison
    const currentDay = DateTime.fromSeconds(currentItem.day)
    const previousDay = DateTime.fromSeconds(previousItemInGroup.day)

    // Check if the current item's day is exactly one day after the previous item's day
    // AND if the quantities are the same
    // AND if the startTime and endTime are the same
    const isNextDay: boolean =
      currentDay.minus({ days: 1 }).toSeconds() === previousDay.toSeconds()
    const hasSameQuantity: boolean =
      currentItem.quantity === previousItemInGroup.quantity
    const hasSameStartAndEndTime =
      currentItem.startTime === referenceStartTime &&
      currentItem.endTime === referenceEndTime

    if (isNextDay && hasSameQuantity && hasSameStartAndEndTime) {
      // If conditions met, add the current item to the current group
      currentGroup.items.push(currentItem)
      // Update the end date of the current group
      currentGroup.endDate = currentItem.day
      currentGroup.totalPrice += currentItem.price
    } else {
      groups.push(currentGroup)
      // Otherwise, start a new group with the current item
      currentGroup = {
        endDate: currentItem.day,
        endTime: currentItem.endTime,
        items: [currentItem],
        quantity: currentItem.quantity,
        startDate: currentItem.day,
        startTime: currentItem.startTime,
        totalPrice: currentItem.price,
      }

      // Reset reference times for the new group
      referenceStartTime = currentItem.startTime
      referenceEndTime = currentItem.endTime
    }
  }
  groups.push(currentGroup)

  return groups.filter((group) => group.quantity !== 0)
}
